<script lang="ts">
  import { onMount } from 'svelte';
  import { getDrawerStore, getModalStore, getToastStore, Tab, TabGroup } from '@skeletonlabs/skeleton';
  import { type ErrorResponse, type IdName, MenuConfig } from '$lib/models/types';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import { clientStore } from '$lib/stores/clientStore';
  import { deleteClientStore } from '$lib/stores/deleteClientStore';
  import { authStore } from '$lib/stores/authStore';
  import { aliiConnectTypeStore } from '$lib/stores/aliiConnectTypeStore';
  import { featureFlagStore } from '$lib/stores/featureFlagStore';
  import type { GetAliiFeatureByNameResponse } from '$lib/models/alii-features/types';
  import { DeleteClientItems, type GetClientDeleteSummaryResponse, type GetClientDeleteDetailsResponse, type ClientDeleteResponse, type DeleteClientInformation, type DeleteClientInformationResponse } from '$lib/models/deleteclient/types';
  import { UsersThree, Check } from 'phosphor-svelte';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import Button from '$lib/components/common/button/Button.svelte';

  export let id: number;

  let isAdminDelete: boolean = false;

  const toastrStore = getToastStore();
  const drawerStore = getDrawerStore();
  const modalStore = getModalStore();

  let loading: boolean = true;

  let clientName: string | undefined;
  let clientStatus: number;
  let connectionTypeId: number;
  let clientId: number;

  let connectionTypes: IdName[] = [];
  $: connectionTypeName = connectionTypes.find((type) => type.id === connectionTypeId)?.name ?? '';

  const CCRFeatureFlagName = 'Web_CanAccessCreditCardRec';
  const ReceiptTrayFeatureFlagName = 'Web_CanAccessReceiptTray';
  const BasiqFeatureFlagName = 'Web_EnableBasiq';

  let deleteSummary: string = '';
  let deleteInformation: DeleteClientInformation | undefined;
  let loadingDetails: boolean = false;
  let deletingClient: boolean = false;

  let loadingLegacyBlobStorage: boolean = false;
  let deletingLegacyBlobStorage: boolean = false;

  $: canClose = !loadingLegacyBlobStorage && !deletingLegacyBlobStorage && !loadingDetails && !deletingClient;

  function getSummaryCellClass(itemType: DeleteClientItems): string {
    const item = deleteInformation?.items.find((i) => i.type === itemType);

    if (!item) {
      return 'summary-cell summary-cell-error';
    }

    if (!item.hasItem) {
      return 'summary-cell summary-cell-blue';
    }

    if (!item.isDeleted) {
      return 'summary-cell summary-cell-warning';
    }

    return 'summary-cell summary-cell-success';
  }

  function informationHasItemType(itemType: DeleteClientItems): boolean {
    const item = deleteInformation?.items.find((i) => i.type === itemType);
    return item && item.hasItem;
  }

  function informationHasDeletedItemType(itemType: DeleteClientItems): boolean {
    const item = deleteInformation?.items.find((i) => i.type === itemType);
    return item && item.hasItem && item.isDeleted;
  }

  async function getDeleteSummary(): Promise<void> {
    try {
      const result: GetClientDeleteSummaryResponse | ErrorResponse = await deleteClientStore.fetchDeleteSummary(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        deleteSummary = result.summary;
      }
    } catch (err) {
      console.error('getDeleteSummary failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching client delete summary.',
        background: 'bg-error-500',
      });
    }
  }

  async function getDeleteInformation(): Promise<void> {
    try {
      const result: DeleteClientInformation | ErrorResponse = await deleteClientStore.fetchDeleteInformation(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        if (result.error) {
          toastrStore.trigger({
            message: result.error,
            background: 'bg-error-500',
          });
        }
        deleteInformation = result.information;
      }
    } catch (err) {
      console.error('getDeleteInformation failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching client delete information.',
        background: 'bg-error-500',
      });
    }
  }

  async function getClientDetails(): Promise<void> {
    try {
      const result: GetClientByIdResponse | ErrorResponse = await clientStore.fetchById(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        clientName = result.clientName;
        clientStatus = result.clientStatus;
        connectionTypeId = result.connectionTypeId;
        clientId = String(result.id as number);
      }
    } catch (err) {
      console.error('getClientDetails failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching client details.',
        background: 'bg-error-500',
      });
    }
  }

  async function getDeleteDetails(): Promise<void> {
    loadingDetails = true;
    try {
      const result: GetClientDeleteDetailsResponse | ErrorResponse = await deleteClientStore.fetchDeleteDetails(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
        return;
      }

      loadingDetails = false;
      modalStore.trigger({
        type: 'component',
        component: 'deleteDetailsModal',
        meta: {
          title: 'Delete Primary SQL Records Details',
          items: result.items,
          error: result.error,
          fileName: `${clientName ?? 'client'}-delete-details.txt`,
          downloadLabel: 'Download records to be deleted',
        },
      });
    } catch (err) {
      console.error('getDeleteDetails failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching client delete details.',
        background: 'bg-error-500',
      });
    } finally {
      loadingDetails = false;
    }
  }

  function onDelete() {
    const pendingItems = deleteInformation?.items.filter((item) => item.type !== DeleteClientItems.ClientSQLRecords && item.hasItem && !item.isDeleted) ?? [];
    if (pendingItems.length > 0) {
      toastrStore.trigger({
        message: `The following items need to be actioned before this client can be deleted: ${pendingItems.map((item) => item.type).join(', ')}.`,
        background: 'bg-error-500',
      });
      return;
    }

    modalStore.trigger({
      type: 'confirm',
      title: 'Delete Primary SQL Records Client',
      body: `Delete of the client "${clientName}" cannot be undone.<br>Ensure you have actioned all related items, as this will remove all records and the client from the Database.`,
      response: (confirmed: boolean) => {
        if (confirmed) {
          onDeleteConfirm()
        }
      },
    });
  }
  async function onDeleteConfirm() {
    modalStore.trigger({
      type: 'confirm',
      title: 'Delete Primary SQL Records Client Final Confirmation',
      body: `Are you sure you want to continue with the delete of the client "${clientName}"?<br>Upon confirmation the client and all related records will be permanently deleted.`,
      buttonTextCancel: 'Keep the client',
      buttonTextConfirm: 'Delete forever',
      response: (confirmed: boolean) => {
        if (confirmed) {
          deleteClient();
        }
      },
    });
  }
  async function deleteClient(): Promise<void> {
    deletingClient = true;
    try {
      const result: ClientDeleteResponse | ErrorResponse = await deleteClientStore.deleteClient(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
        return;
      }

      deletingClient = false;
      setTimeout(() => {
        modalStore.trigger({
          type: 'component',
          component: 'deleteDetailsModal',
          meta: {
            title: 'Deleted Primary SQL Records Details',
            items: result.items,
            error: result.error,
            fileName: `${clientName ?? 'client'}-delete-details.txt`,
            downloadLabel: 'Download deleted records',
          },
          response: () => {
            onCloseClick();
          },
        });
      }, 200);
    } catch (err) {
      console.error('deleteClient failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while deleting the client.',
        background: 'bg-error-500',
      });
    } finally {
      deletingClient = false;
    }
  }

  async function getLegacyBlobStorageDetails(): Promise<void> {
    loadingLegacyBlobStorage = true;
    try {
      const result: GetClientDeleteDetailsResponse | ErrorResponse = await deleteClientStore.fetchLegacyBlobStorageDetails(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
        return;
      }

      loadingLegacyBlobStorage = false;
      modalStore.trigger({
        type: 'component',
        component: 'deleteDetailsModal',
        meta: {
          title: 'Primary Legacy Blob storage Delete Details',
          items: result.items,
          error: result.error,
          fileName: `${clientName ?? 'client'}-legacy-blob-storage-delete-details.txt`,
          downloadLabel: 'Download blobs to be deleted',
        },
      });
    } catch (err) {
      console.error('getLegacyBlobStorageDetails failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching client legacy blob storage details.',
        background: 'bg-error-500',
      });
    } finally {
      loadingLegacyBlobStorage = false;
    }
  }
  function onDeleteLegacyBlobStorage() {
    modalStore.trigger({
      type: 'confirm',
      title: 'Delete Client Primary Legacy Blob Storage',
      body: `Delete of the client "${clientName}" legacy blob storage cannot be undone.<br>This will remove all blobs from the client's legacy blob storage.`,
      response: (confirmed: boolean) => {
        if (confirmed) {
          onDeleteLegacyBlobStorageConfirm()
        }
      },
    });
  }
  async function onDeleteLegacyBlobStorageConfirm() {
    modalStore.trigger({
      type: 'confirm',
      title: 'Delete Client Primary Legacy Blob Storage Final Confirmation',
      body: `Are you sure you want to continue with the delete of the client "${clientName}" legacy blob storage?<br>Upon confirmation the client's legacy blob storage will be permanently deleted.`,
      buttonTextCancel: 'Keep the blob storage',
      buttonTextConfirm: 'Delete forever',
      response: (confirmed: boolean) => {
        if (confirmed) {
          deleteLegacyBlobStorage();
        }
      },
    });
  }
  async function deleteLegacyBlobStorage(): Promise<void> {
    deletingLegacyBlobStorage = true;
    try {
      const result: ClientDeleteResponse | ErrorResponse = await deleteClientStore.deleteLegacyBlobStorage(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
        return;
      }

      deletingLegacyBlobStorage = false;
      setTimeout(() => {
        modalStore.trigger({
          type: 'component',
          component: 'deleteDetailsModal',
          meta: {
            title: 'Deleted Primary Legacy Blob Storage Details',
            items: result.items,
            error: result.error,
            fileName: `${clientName ?? 'client'}-delete-legacy-blob-storage-details.txt`,
            downloadLabel: 'Download deleted blobs',
          },
          response: () => {
            getFormDetails();
          },
        });
      }, 200);
    } catch (err) {
      console.error('deleteLegacyBlobStorage failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while deleting the client legacy blob storage.',
        background: 'bg-error-500',
      });
    } finally {
      deletingLegacyBlobStorage = false;
    }
  }
  
  async function onItemDelete(itemType: DeleteClientItems) {
    modalStore.trigger({
      type: 'confirm',
      title: `Delete ${itemType} Final Confirmation`,
      body: `Are you sure you want to mark the deletion of ${itemType} as completed"?`,
      buttonTextConfirm: 'Mark delete as Completed',
      response: (confirmed: boolean) => {
        if (confirmed) {
          markItemAsDeleted(itemType);
        }
      },
    });
  }

  async function markItemAsDeleted(itemType: DeleteClientItems) {
    try {
      await deleteClientStore.markItemAsDeleted(id, itemType);
      toastrStore.trigger({
        message: `${itemType} marked as deleted.`,
        background: 'bg-success-500',
      });
      await getFormDetails();
    } catch (err) {
      console.error('markItemAsDeleted failed:', err);
      toastrStore.trigger({
        message: `An error occurred while marking ${itemType} as deleted.`,
        background: 'bg-error-500',
      });
    }
  }

  async function getFormDetails(): Promise<void> {
    loading = true;
    try {
      await authStore.checkAdminDeleteStatus();
      connectionTypes = await aliiConnectTypeStore.get();
      await getClientDetails();
      await getDeleteSummary();
      await getDeleteInformation();
    } catch (err) {
      console.error('getFormDetails failed:', err);
      toastrStore.trigger({
        message: 'An error occurred while fetching delete form details.',
        background: 'bg-error-500',
      });
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    const unsubscribeIsAdminDelete = authStore.isAdminDelete.subscribe((value) => {
      isAdminDelete = value;
    });
    return unsubscribeIsAdminDelete;
  });

  onMount(async () => {
    await getFormDetails();
  });

  function onCloseClick() {
    if (!canClose) {
      return;
    }
    drawerStore.close();
  }

</script>

<div class="card flex h-full flex-col">
  <header class="flex justify-between border-b p-5">
    <div class="inline-flex items-center gap-2 p-4 text-2xl font-bold">
      <span>
        <UsersThree />
      </span>
      <span> Delete Client </span>
    </div>
    <div class="content-center">
      <IconButton icon="x" disabled={!canClose} onClick={onCloseClick} />
    </div>
  </header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if loading}
      <p>Loading...</p>
    {:else}
      <div class="p-5">
        <div class="mb-5 flex items-center justify-between text-xl font-bold text-primary-500">
          <span>{clientName} ({id})</span>
        </div>
        <div class="mb-5 space-y-1 text-sm text-gray-600">
          <p>Review the items below before finally deleting this client.</p>
          <p>Blue boxes indicate no action is required, while yellow boxes highlight items that must be addressed before the client can be safely removed. It will go green once it is actioned.</p>
          <p>Ensure the relevant items are actioned before deleting the client.</p>
        </div>
        {#if loadingDetails}
          <span class="ml-3 text-blue-600 font-semibold">Loading details...</span>
        {:else if deletingClient}
          <span class="ml-3 text-blue-600 font-semibold">Deleting Client {clientName}...</span>
        {:else if loadingLegacyBlobStorage}
          <span class="ml-3 text-blue-600 font-semibold">Loading legacy blob storage details...</span>
        {:else if deletingLegacyBlobStorage}
          <span class="ml-3 text-blue-600 font-semibold">Deleting legacy blob storage for Client {clientName}...</span>
        {:else}
          <div class="max-h-[60vh] overflow-y-auto">
            <table class="w-full table-auto border-separate border-spacing-y-3">
              <thead>
                <tr>
                  <th class="w-auto px-4 text-left text-sm font-semibold text-gray-600">Item</th>
                  <th class="w-px whitespace-nowrap px-4 text-left text-sm font-semibold text-gray-600">&nbsp;</th>
                  <th class="w-px whitespace-nowrap px-4 text-left text-sm font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.NewWorldIntegration)}>
                    {#if informationHasItemType(DeleteClientItems.NewWorldIntegration)}
                      <p>This client has an active <strong>{connectionTypeName}</strong> connection that will need to be disconnected as part of the deletion.</p>
                      <p class="mt-1">For some integrations, this can be done on the Integration Tab on the Client Details.</p>
                      <p class="mt-1">Where it is not available, it will need to be done manually for now.</p>
                    {:else}
                      <p>Client.ConnectTypeId is not new world integrations.</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.NewWorldIntegration)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.NewWorldIntegration)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.NewWorldIntegration)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.CCRecBlobStorage)}>
                    {#if informationHasItemType(DeleteClientItems.CCRecBlobStorage)}
                      <p>This client has CCRec blob storage that needs be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually clean up the SFTP blob storage containers ccr-user-ui-config, ccred-transactions files, manual-transaction-imports, statement-export.</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no CCRec blob storage (FF <strong>{CCRFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.CCRecBlobStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.CCRecBlobStorage)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.CCRecBlobStorage)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.CCRecSQLRecords)}>
                    {#if informationHasItemType(DeleteClientItems.CCRecSQLRecords)}
                      <p>This client has CCRec SQL records that need be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually run the sql script (alii.services.creditcardrec\sql scripts\Delete all data for a client.sql).</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no CCRec SQL records (FF <strong>{CCRFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.CCRecSQLRecords)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.CCRecSQLRecords)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.CCRecSQLRecords)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.ReceiptTrayBlobStorage)}>
                    {#if informationHasItemType(DeleteClientItems.ReceiptTrayBlobStorage)}
                      <p>This client has Receipt Tray blob storage that needs be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually clean the blob storage containers receipt-files.</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no Receipt Tray blob storage (FF <strong>{ReceiptTrayFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.ReceiptTrayBlobStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.ReceiptTrayBlobStorage)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.ReceiptTrayBlobStorage)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.ReceiptTraySQLRecords)}>
                    {#if informationHasItemType(DeleteClientItems.ReceiptTraySQLRecords)}
                      <p>This client has Receipt Tray SQL records that need to be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually run the sql script (alii.services.creditcardrec\sql scripts\Delete all data for a client.sql).</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no Receipt Tray SQL records to delete (FF <strong>{ReceiptTrayFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.ReceiptTraySQLRecords)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.ReceiptTraySQLRecords)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.ReceiptTraySQLRecords)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.BasiqBlobStorage)}>
                    {#if informationHasItemType(DeleteClientItems.BasiqBlobStorage)}
                      <p>This client has Basiq blob storage that needs be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually clean the blob storage containers basiq-data.</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no Basiq blob storage (FF <strong>{BasiqFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.BasiqBlobStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.BasiqBlobStorage)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.BasiqBlobStorage)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.BasiqSQLRecords)}>
                    {#if informationHasItemType(DeleteClientItems.BasiqSQLRecords)}
                      <p>This client has Basiq SQL records that need to be removed as part of the deletion.</p>
                      <p class="mt-1">This feature is pending, manually run the sql script (alii.services.creditcardrec\sql scripts\Delete all basiq data for a client.sql).</p>
                      <p class="mt-1">Future state - add AdminApi to CCRec.Api.</p>
                    {:else}
                      <p>This client has no Basiq SQL records (FF <strong>{BasiqFeatureFlagName}</strong> is false).</p>
                    {/if}
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.BasiqSQLRecords)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.BasiqSQLRecords)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.BasiqSQLRecords)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.OtherBlobStorage)}>
                    <p>Any other other blob storage will need to be manually deleted.</p>
                    <p class="mt-1">This feature is pending, manually clean the blob storage containers including datasync-metadata, service-bus-attachments.</p>
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.OtherBlobStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.OtherBlobStorage)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.OtherBlobStorage)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.OtherTableStorage)}>
                    <p>Any other table storage will need to be manually deleted.</p>
                    <p class="mt-1">This feature is pending, manually clean the table storage including ClientUserEntity, DataSyncMetaDataEntity, DataSyncMetaDataArchiveEntity, DataSyncMetaDataOptions, InvoiceLineUIV2TableEntity, Localisation, PurchaseOrderLineUIV2TableEntity, SidebarTableEntity.</p>
                  </td>
                  <td class="action-cell"></td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.OtherTableStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.OtherTableStorage)}
                      <Button label="Mark Deleted" colour="error" size="medium" onClick={() => onItemDelete(DeleteClientItems.OtherTableStorage)} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.LegacyBlobStorage)}>
                    {#if informationHasItemType(DeleteClientItems.LegacyBlobStorage)}
                      <p>The legacy primary blob storage associated with this client will need to be deleted.</p>
                      <p class="mt-1">This includes Invoice Attachments, PO Files and EC Files amongst others.</p>
                      <p class="mt-1">Use Details to get the items that will be cleanup up. Then when reviewed, Delete will remove the legacy primary blob storage for this client.</p>
                    {:else}
                      <p>This client has no legacy primary blob storage as identified by the records in SQL Records.</p>
                    {/if}
                  </td>
                  <td class="action-cell">
                    {#if informationHasItemType(DeleteClientItems.LegacyBlobStorage) && !informationHasDeletedItemType(DeleteClientItems.LegacyBlobStorage)}
                      <Button label="Details" colour="primary" size="medium" onClick={() => getLegacyBlobStorageDetails()} />
                    {/if}
                  </td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.LegacyBlobStorage)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.LegacyBlobStorage)}
                      <Button label="Delete" colour="error" size="medium" onClick={() => onDeleteLegacyBlobStorage()} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
                <tr>
                  <td class={getSummaryCellClass(DeleteClientItems.ClientSQLRecords)}>
                    <p>Before deleting the client, ensure that all associated resources are properly handled.</p>
                    <p class="mt-1">The current data includes: {deleteSummary}.</p>
                    <p class="mt-1">Use Details to get the items that will be cleaned up. Then when reviewed, Delete will remove the SQL records for this client.</p>
                  </td>
                  <td class="action-cell">
                    {#if informationHasItemType(DeleteClientItems.ClientSQLRecords) && !informationHasDeletedItemType(DeleteClientItems.ClientSQLRecords)}
                      <Button label="Details" colour="primary" size="medium" onClick={() => getDeleteDetails()} />
                    {/if}
                  </td>
                  <td class="action-cell">
                    {#if informationHasDeletedItemType(DeleteClientItems.ClientSQLRecords)}
                      <Check size={24} weight="bold" class="text-success-500" />
                    {:else if informationHasItemType(DeleteClientItems.ClientSQLRecords)}
                      <Button label="Delete" colour="error" size="medium" onClick={() => onDelete()} />
                    {:else}
                      <span class="block text-left text-blue-400">N/A</span>
                    {/if}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .summary-cell {
    @apply border p-4;

    &.summary-cell-error {
      @apply border-error-500 bg-error-100 text-error-700;
    }
    &.summary-cell-warning {
      @apply border-warning-500 bg-warning-100 text-warning-700;
    }
    &.summary-cell-success {
      @apply border-success-500 bg-success-100 text-success-700;
    }
    &.summary-cell-blue{
      @apply border-blue-500 bg-blue-100 text-blue-700;
    }
  }

  .action-cell {
    @apply w-px whitespace-nowrap border border-gray-300 p-4 text-right;
  }
</style>
